import { useCallback, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, SearchInput, StatusMessage } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { AlertTriangle, Plus, SearchX, Users } from 'lucide-react';

import Can from '../components/guards/Can';
import { GroupsGrid, GroupsGridSkeleton, NewGroupModal } from '../components/groups';
import { GROUP_PERMISSIONS } from '../constants/groupPermissions';
import { useGroupMutations } from '../hooks/useGroupMutations';
import { useGroups } from '../hooks/useGroups';

/**
 * Groups tab: card grid, search (commit on Enter) and the New group modal. A card and its Manage
 * group button open the group detail page; there is no preview slide-out.
 */
function GroupsPage() {
  const { t } = useTranslation('people-access');
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { groups, isLoading, error, search, setSearch, reload } = useGroups();
  const { createGroup } = useGroupMutations();
  const [draft, setDraft] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const handleOpen = useCallback(
    (groupId) => navigate(`${pathname.replace(/\/+$/, '')}/${groupId}`),
    [navigate, pathname],
  );

  const handleSearchKeyDown = useCallback(
    (event) => {
      if (event.key === 'Enter') setSearch(draft.trim());
    },
    [draft, setSearch],
  );

  const handleClearSearch = useCallback(() => {
    setDraft('');
    setSearch('');
  }, [setSearch]);

  const handleOpenCreate = useCallback(() => setIsCreating(true), []);
  const handleCloseCreate = useCallback(() => setIsCreating(false), []);

  const handleCreate = useCallback(
    async (payload) => {
      const group = await createGroup(payload);
      // A filter that hides the new group would make it look lost: show everything again.
      if (search) handleClearSearch();
      else reload();
      return group;
    },
    [createGroup, reload, search, handleClearSearch],
  );

  const renderResults = () => {
    // Also on a retry after a failure: Try again shows the skeleton until the request ends.
    if (isLoading && groups.length === 0) return <GroupsGridSkeleton />;
    if (error) {
      return (
        <div className="flex flex-col items-center gap-3">
          <StatusMessage icon={AlertTriangle} title={error} subtitle="" />
          <Button variant="secondary" size="sm" onClick={reload}>
            {t('groups.list.retry')}
          </Button>
        </div>
      );
    }
    if (groups.length === 0) {
      return search ? (
        <StatusMessage
          icon={SearchX}
          title={t('groups.list.noMatchTitle')}
          subtitle={t('groups.list.noMatchSubtitle')}
        />
      ) : (
        <StatusMessage
          icon={Users}
          title={t('groups.list.emptyTitle')}
          subtitle={t('groups.list.emptySubtitle')}
        />
      );
    }
    return <GroupsGrid groups={groups} onOpen={handleOpen} />;
  };

  return (
    <div data-testid="page-groups" className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <SearchInput
          className="w-full max-w-xs"
          aria-label={t('groups.list.searchAria')}
          placeholder={t('groups.list.searchPlaceholder')}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={handleSearchKeyDown}
          onClear={handleClearSearch}
        />
        <Can permission={GROUP_PERMISSIONS.CREATE}>
          <Button size="md" leftIcon={Plus} onClick={handleOpenCreate}>
            {t('groups.list.newGroup')}
          </Button>
        </Can>
      </div>
      {renderResults()}
      <NewGroupModal isOpen={isCreating} onClose={handleCloseCreate} onCreate={handleCreate} />
    </div>
  );
}

export default GroupsPage;
